/**
 * Backfill Grade on Classes and Students
 *
 * 1. Parse grade from class name (e.g., "7A" → grade "7", "8B" → grade "8")
 * 2. Set grade on classes that don't have one yet
 * 3. Set grade on active students from their class's grade
 * 4. Set grade on graduated students (they were grade 7 → now grade 8 after promotion)
 *
 * USAGE: await window.backfillGrades()
 */

import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase.js';

// Parse grade from class name: "7A" → "7", "8B" → "8", "10C" → "10", "R1" → "R"
function parseGradeFromName(name) {
  if (!name) return null;
  const trimmed = name.trim();

  // Match "R" at start (Grade R classes like "R1", "RA")
  if (/^R\d?[A-Za-z]?$/i.test(trimmed) || /^R$/i.test(trimmed)) {
    return 'R';
  }

  // Match number at start: "7A" → "7", "10C" → "10", "12B" → "12"
  const match = trimmed.match(/^(\d{1,2})/);
  if (match) {
    const num = parseInt(match[1]);
    if (num >= 1 && num <= 12) return String(num);
  }

  return null;
}

function getSchoolId() {
  const keys = Object.keys(localStorage);
  const contextKey = keys.find(k => k.startsWith('swan_school_context_'));
  if (contextKey) {
    const contextData = localStorage.getItem(contextKey);
    if (contextData) return JSON.parse(contextData).schoolId;
  }
  return null;
}

async function backfillGrades() {
  console.log('🎓 Backfilling grades on classes and students...\n');

  try {
    const schoolId = getSchoolId();
    if (!schoolId) {
      console.error('No schoolId found');
      return;
    }

    // Step 1: Backfill grades on classes
    const classesRef = collection(db, `schools/${schoolId}/classes`);
    const classesSnap = await getDocs(classesRef);
    const classGradeMap = {}; // classId → grade
    let classesUpdated = 0;
    let classesSkipped = 0;

    console.log(`📚 Processing ${classesSnap.size} classes...`);

    for (const classDoc of classesSnap.docs) {
      const data = classDoc.data();

      if (data.grade) {
        // Already has grade
        classGradeMap[classDoc.id] = data.grade;
        classesSkipped++;
        continue;
      }

      const parsed = parseGradeFromName(data.name);
      if (parsed) {
        classGradeMap[classDoc.id] = parsed;
        await updateDoc(doc(db, `schools/${schoolId}/classes`, classDoc.id), { grade: parsed });
        classesUpdated++;
        console.log(`  ✅ ${data.name} → Grade ${parsed}`);
      } else {
        console.log(`  ⚠️  ${data.name} → could not parse grade`);
      }
    }

    console.log(`\n📚 Classes: ${classesUpdated} updated, ${classesSkipped} already had grade\n`);

    // Step 2: Backfill grades on students
    const studentsRef = collection(db, `schools/${schoolId}/students`);
    const studentsSnap = await getDocs(studentsRef);
    let studentsUpdated = 0;
    let studentsSkipped = 0;
    let studentsNoClass = 0;

    console.log(`👨‍🎓 Processing ${studentsSnap.size} students...`);

    for (const studentDoc of studentsSnap.docs) {
      const data = studentDoc.data();

      if (data.grade) {
        studentsSkipped++;
        continue;
      }

      // Try to get grade from their class
      const grade = data.classId ? classGradeMap[data.classId] : null;

      if (grade) {
        await updateDoc(doc(db, `schools/${schoolId}/students`, studentDoc.id), { grade });
        studentsUpdated++;
        if (studentsUpdated <= 20 || studentsUpdated % 25 === 0) {
          console.log(`  ✅ ${data.firstName} ${data.surname} (${data.className}) → Grade ${grade}`);
        }
      } else if (data.classId) {
        // Has class but we couldn't determine grade
        const parsedFromClassName = parseGradeFromName(data.className);
        if (parsedFromClassName) {
          await updateDoc(doc(db, `schools/${schoolId}/students`, studentDoc.id), { grade: parsedFromClassName });
          studentsUpdated++;
          console.log(`  ✅ ${data.firstName} ${data.surname} (${data.className}) → Grade ${parsedFromClassName} (from name)`);
        } else {
          studentsNoClass++;
        }
      } else {
        studentsNoClass++;
      }
    }

    console.log(`\n👨‍🎓 Students: ${studentsUpdated} updated, ${studentsSkipped} already had grade, ${studentsNoClass} no class/grade`);
    console.log('\n✅ Grade backfill complete!');

    return {
      success: true,
      classes: { updated: classesUpdated, skipped: classesSkipped },
      students: { updated: studentsUpdated, skipped: studentsSkipped, noClass: studentsNoClass }
    };

  } catch (error) {
    console.error('❌ Error:', error);
    throw error;
  }
}

export { backfillGrades };
